// Settings.tsx — platform settings: landing grid + Operator SSO + Global AI.
//
// OPERATOR SSO IS A PRIVILEGE-ESCALATION SURFACE. The backend hard-forces the
// only safe shape (provider_type=microsoft, platform tenant, auto_provision=false,
// default_role=requester) and rejects attempts to widen any of them. This UI does
// not expose those fields at all — not as disabled inputs, not as hidden defaults.
// Rendering a control for a value the server refuses to accept invites someone to
// try, and the attempt is the thing worth preventing. The panel explains the
// default-deny posture instead.
//
// The client secret follows the write-only pattern: the server returns only
// `has_secret`, and PSecretField has no prop through which a value could be
// passed in.

import React, { useCallback, useEffect, useState } from "react";
import { PT } from "../../platformTheme";
import { pget, pmutate, perr } from "./api";
import {
  PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner, PTable, PRow,
  PReasonPrompt, PInput, PCheck, PSecretField, PGrid, th, td,
} from "./ui";
// The routing catalog lives in its own file: it is the operator's only path to
// change which model runs a tier, and it is substantially larger than the SSO
// panel it used to sit beside.
import { GlobalAi } from "./GlobalAi";

type Panel = null | "sso" | "ai";

export const Settings: React.FC = () => {
  const [panel, setPanel] = useState<Panel>(null);

  if (panel === "sso") return <OperatorSsoPanel onBack={() => setPanel(null)} />;
  if (panel === "ai") return <GlobalAi onBack={() => setPanel(null)} />;

  const CARDS: { id: Exclude<Panel, null>; title: string; body: string }[] = [
    { id: "sso", title: "Operator SSO", body: "Microsoft Entra (OIDC) sign-in for platform operators. Default-deny: an Entra user who is not already an operator is rejected." },
    { id: "ai", title: "Global AI", body: "Routing catalog per tier and role, and the platform default provider keys every customer without a BYO key runs on." },
  ];

  return (
    <PSection title="Settings">
      <PGrid min={260}>
        {CARDS.map((c) => (
          <PCard key={c.id} onClick={() => setPanel(c.id)}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{c.title}</div>
            <div style={{ fontSize: 12, color: PT.txM, lineHeight: 1.5 }}>{c.body}</div>
          </PCard>
        ))}
      </PGrid>
      <div style={{ fontSize: 11.5, color: PT.txD, marginTop: 14 }}>
        Email &amp; notifications, security policy, and data retention are not shown: they have no
        operator-level endpoint today, and a settings card that cannot save anything is worse than
        an absent one.
      </div>
    </PSection>
  );
};

// ── Operator SSO ─────────────────────────────────────────────────────────────

const OperatorSsoPanel: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [cfg, setCfg] = useState<any>(null);
  const [configured, setConfigured] = useState(false);
  const [clientId, setClientId] = useState("");
  const [directory, setDirectory] = useState("");
  const [domains, setDomains] = useState("");
  const [active, setActive] = useState(false);
  const [secret, setSecret] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [prompt, setPrompt] = useState<null | { title: string; detail: React.ReactNode; danger?: boolean; run: (r: string) => Promise<void> }>(null);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    const r = await pget("/operator-sso");
    if (!r.ok) { setErr(perr(r, "Could not load the operator SSO configuration")); setCfg({}); return; }
    const c = r.data?.config || null;
    setConfigured(!!r.data?.configured);
    setCfg(c || {});
    setClientId(c?.client_id || "");
    setDirectory(c?.tenant_id_external || "");
    setDomains((c?.allowed_email_domains || []).join(", "));
    setActive(!!c?.is_active);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (reason: string) => {
    if (!prompt) return;
    setBusy(true); setPErr("");
    try { await prompt.run(reason); setPrompt(null); await load(); }
    catch (e: any) { setPErr(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const hasSecret = !!cfg?.has_secret;

  if (cfg === null) return <PSpinner />;

  return (
    <PSection title="Operator SSO · Microsoft Entra (OIDC)" right={<PBtn small onClick={onBack}>← Settings</PBtn>}>
      <PBanner>{err}</PBanner>
      <PBanner tone="ok">{msg}</PBanner>

      <PBanner tone="info">
        <strong>Default-deny by construction.</strong> The server forces
        <code> auto_provision_enabled=false</code> and <code>default_role=requester</code>, and
        rejects any attempt to set them otherwise. An arbitrary Entra user who signs in gets no
        account and no operator access — SSO authenticates existing operators, it never creates one.
      </PBanner>

      <PCard>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
          {configured ? <PBadge tone="ok">Configured</PBadge> : <PBadge tone="neutral">Not configured</PBadge>}
          {configured && (active ? <PBadge tone="ok">Active</PBadge> : <PBadge tone="warn">Inactive</PBadge>)}
        </div>

        <PInput label="Application (client) ID" value={clientId} onChange={setClientId} mono />
        <PInput label="Directory (tenant) ID" value={directory} onChange={setDirectory} mono hint="The Entra directory `tid`." />
        <PSecretField
          label="Client secret"
          configured={hasSecret}
          value={secret}
          onChange={setSecret}
          required={!configured}
          hint="Encrypted at rest via ssoCrypto. Never returned by the API, never logged."
        />
        <PInput
          label="Allowed email domains" value={domains} onChange={setDomains}
          placeholder="kontractai.com, example.com"
          hint="Comma-separated. Lower-cased and trimmed on save."
        />
        <PCheck checked={active} onChange={setActive} label="Active" />

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <PBtn
            v="primary"
            disabled={!configured && !secret.trim()}
            title={!configured && !secret.trim() ? "A client secret is required to create the provider" : undefined}
            onClick={() => setPrompt({
              title: "Save operator SSO configuration",
              detail: (
                <>
                  Writing the operator SSO provider row.
                  {secret.trim() && <div style={{ marginTop: 7 }}>The client secret <strong>will be replaced</strong>.</div>}
                  {!secret.trim() && configured && <div style={{ marginTop: 7, color: PT.txM }}>The stored secret is kept.</div>}
                </>
              ),
              run: async (reason) => {
                const body: Record<string, unknown> = {
                  client_id: clientId.trim(),
                  tenant_id_external: directory.trim(),
                  allowed_email_domains: domains.split(",").map((d) => d.trim().toLowerCase()).filter(Boolean),
                  is_active: active,
                };
                // Only send the secret when replacing it — sending an empty string
                // would be indistinguishable from "clear it" to a future handler.
                if (secret.trim()) body.client_secret = secret.trim();
                const r = await pmutate("PUT", "/operator-sso", reason, body);
                if (!r.ok) throw new Error(perr(r));
                setSecret("");
                setMsg("Operator SSO configuration saved.");
              },
            })}
          >Save</PBtn>

          {configured && (
            <PBtn
              v="danger"
              onClick={() => setPrompt({
                title: "Deactivate operator SSO",
                danger: true,
                detail: (
                  <>
                    Sets <code>is_active=false</code>. Operators fall back to password sign-in.
                    <div style={{ marginTop: 7, color: PT.txM }}>
                      The row is not deleted and the secret is retained — reactivating does not
                      require re-entering it.
                    </div>
                  </>
                ),
                run: async (reason) => {
                  const r = await pmutate("DELETE", "/operator-sso", reason);
                  if (!r.ok) throw new Error(perr(r));
                  setMsg("Operator SSO deactivated.");
                },
              })}
            >Deactivate</PBtn>
          )}
        </div>
      </PCard>

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={run}
      />
    </PSection>
  );
};
